import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import swal from "sweetalert";
import moment from "moment";
import {
  twoFactorSettings,
  changePassword,
  getSessions,
  revokeSession,
  revokeOtherSessions,
} from "../../../../../services";
import SetupTwoFactorModal from "../../../../../components/SetupTwoFactorModal";
import SetupAuthenticatorModal from "../../../../../components/SetupAuthenticatorModal";
import "../settings.css";

/* ── Password policy (matches the reset-password screen and the backend's
      `accountPassword` Joi rule: 8+ chars, at least 1 letter and 1 number). ── */
const PWD_MIN_LENGTH = 8;

const passwordRules = (value = "") => [
  {
    key: "length",
    label: `At least ${PWD_MIN_LENGTH} characters`,
    ok: value.length >= PWD_MIN_LENGTH,
  },
  { key: "letter", label: "One letter", ok: /[a-zA-Z]/.test(value) },
  { key: "number", label: "One number", ok: /\d/.test(value) },
];

/** 0-4 strength score. The 3 required rules, plus a bonus for real length + symbols. */
const passwordScore = (value = "") => {
  if (!value) return 0;
  const met = passwordRules(value).filter((r) => r.ok).length;
  if (met < 3) return met === 0 ? 1 : met;
  const strong = value.length >= 12 && /[^a-zA-Z0-9]/.test(value);
  return strong ? 4 : 3;
};

const SCORE_LABELS = ["", "Weak", "Fair", "Good", "Strong"];

const CheckIcon = ({ ok }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {ok ? (
      <polyline points="20 6 9 17 4 12" />
    ) : (
      <circle cx="12" cy="12" r="8" />
    )}
  </svg>
);

const DeviceIcon = ({ type }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {type === "mobile" ? (
      <>
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <line x1="11" y1="18" x2="13" y2="18" />
      </>
    ) : type === "tablet" ? (
      <>
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <line x1="11" y1="18" x2="13" y2="18" />
      </>
    ) : (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    )}
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
    {off && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

/**
 * Security settings panel (redesign).
 *
 * Maps to the existing `Setting/Security/index.jsx`. We REUSE its real wiring:
 *   - 2FA preferences (SMS / Email) + master "enable 2FA access" switch are
 *     persisted through the existing `twoFactorSettings` service
 *     (`POST /auth/two-factor-settings`).
 *   - The reset / setup flow opens the existing `SetupTwoFactorModal`.
 *   - Password change posts to `POST /auth/change-password`, which verifies the
 *     current password before writing the new one.
 *   - Active sessions read `GET /auth/sessions` and revoke through
 *     `DELETE /auth/sessions/:id` and `DELETE /auth/sessions/others`.
 *
 * The original component's same business rules are preserved:
 *   - At least one of SMS / Email must stay enabled (enforced on save).
 *   - Enabling SMS requires a phone number on file.
 *   - The master "Enable two-factor authentication" switch can't be turned on
 *     until 2FA has actually been verified (`twoFactorVerified`), and is gated
 *     to admins (`profile.user.role !== "staff"`) exactly as today.
 *
 * NEW design pieces with NO existing backend are rendered as clearly-disabled
 * "Coming soon" — never faked:
 *   - Password change
 *   - Active sessions
 *
 * Authenticator app (TOTP) is wired to the real `/auth/totp/*` endpoints via
 * `SetupAuthenticatorModal` — enabling opens the QR/verify flow, disabling
 * requires a current code. State is seeded from `profile.user.totpEnabled`.
 *   - Authenticator app (TOTP)
 */
export default function SecurityPanel({ profile: profileProp, role }) {
  const { state } = useLocation();
  const profileFromStore = useSelector((s) => s?.profile?.profile);
  const profile = profileProp || profileFromStore;

  // The original Security component gates the master switch on user.role
  const canManageMasterSwitch = profile?.user?.role !== "staff";

  const is2faVerified = !!profile?.user?.twoFactorVerified;

  const [saving, setSaving] = useState(false);
  const [twoFA, setTwoFA] = useState({ sms: false, email: false });
  const [is2FaAccess, setIs2FaAccess] = useState(false);
  const [authenticatorEnabled, setAuthenticatorEnabled] = useState(false);

  const [setupModalOpened, { open: openSetupModal, close: closeSetupModal }] =
    useDisclosure(false);

  const [
    authenticatorModalOpened,
    { open: openAuthenticatorModal, close: closeAuthenticatorModal },
  ] = useDisclosure(false);
  const [authenticatorModalMode, setAuthenticatorModalMode] = useState("setup");
  // ── Password change ───────────────────────────────────────────────────────
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdVisible, setPwdVisible] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [pwdErrors, setPwdErrors] = useState({});
  const [pwdFormError, setPwdFormError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const rules = passwordRules(pwd.next);
  const score = passwordScore(pwd.next);

  const setPwdField = (key) => (e) => {
    const { value } = e.target;
    setPwd((prev) => ({ ...prev, [key]: value }));
    // Clear the field's error (and any banner) as soon as the user edits it.
    setPwdErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    setPwdFormError("");
    setPwdSuccess("");
  };

  const toggleVisible = (key) =>
    setPwdVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  const validatePwd = () => {
    const next = {};
    if (!pwd.current) next.current = "Enter your current password.";
    if (!pwd.next) {
      next.next = "Enter a new password.";
    } else if (rules.some((r) => !r.ok)) {
      next.next = `Password must be at least ${PWD_MIN_LENGTH} characters and include a letter and a number.`;
    } else if (pwd.next === pwd.current) {
      next.next = "New password must be different from your current password.";
    }
    if (!pwd.confirm) {
      next.confirm = "Re-enter your new password.";
    } else if (pwd.next && pwd.confirm !== pwd.next) {
      next.confirm = "Passwords do not match.";
    }
    setPwdErrors(next);
    return Object.keys(next).length === 0;
  };

  // ── Active sessions ───────────────────────────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState("");
  // Id of the row being revoked, or "others" for the bulk action.
  const [revoking, setRevoking] = useState(null);

  const loadSessions = async () => {
    const res = await getSessions();
    if (res?.success) {
      setSessions(res.sessions);
      setSessionsError("");
    } else {
      setSessionsError(res?.message || "Unable to load your active devices.");
    }
    setSessionsLoading(false);
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const otherSessionCount = sessions.filter((s) => !s.current).length;

  const handleRevokeSession = async (session) => {
    const confirmed = await swal({
      title: "Sign out this device?",
      text: `${session.browser} on ${session.os} will need to sign in again.`,
      icon: "warning",
      buttons: ["Cancel", "Sign out"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setRevoking(session.id);
    const res = await revokeSession(session.id);
    setRevoking(null);

    if (res?.success) {
      // Re-fetch rather than splicing locally, so the list reflects whatever the
      // server actually did.
      await loadSessions();
    } else {
      swal("Couldn't sign out that device", res?.message || "", "error");
    }
  };

  const handleRevokeOthers = async () => {
    const confirmed = await swal({
      title: "Sign out all other devices?",
      text: `${otherSessionCount} other ${
        otherSessionCount === 1 ? "device" : "devices"
      } will need to sign in again. You'll stay signed in here.`,
      icon: "warning",
      buttons: ["Cancel", "Sign out all"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setRevoking("others");
    const res = await revokeOtherSessions();
    setRevoking(null);

    if (res?.success) {
      await loadSessions();
      swal("Done", res?.message || "Other devices signed out.", "success");
    } else {
      swal("Couldn't sign out your other devices", res?.message || "", "error");
    }
  };

  const handleChangePassword = async () => {
    setPwdSuccess("");
    setPwdFormError("");
    if (!validatePwd()) return;

    try {
      setSavingPwd(true);
      const res = await changePassword({
        currentPassword: pwd.current,
        newPassword: pwd.next,
      });

      if (res?.success) {
        setPwd({ current: "", next: "", confirm: "" });
        setPwdVisible({ current: false, next: false, confirm: false });
        setPwdErrors({});
        setPwdSuccess(res.message);
        return;
      }

      // Surface a wrong current password against its own field; everything
      // else reads as a form-level banner.
      if (/current password/i.test(res?.message || "")) {
        setPwdErrors({ current: res.message });
      } else {
        setPwdFormError(res?.message || "Unable to update your password.");
      }
    } finally {
      setSavingPwd(false);
    }
  };

  // Seed 2FA preferences from the hydrated profile (same as existing component)
  useEffect(() => {
    setTwoFA({
      sms: !!profile?.user?.sms2FaEnabled,
      email: !!profile?.user?.email2FaEnabled,
    });
    setIs2FaAccess(!!profile?.user?.is2FaAccess);
    setAuthenticatorEnabled(!!profile?.user?.totpEnabled);
  }, [profile]);

  // location.state.reset2fa auto-opens the setup modal (preserved behavior)
  useEffect(() => {
    if (state?.reset2fa) {
      openSetupModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.reset2fa]);

  const toggleMaster = () => {
    if (!canManageMasterSwitch) return;
    const next = !is2FaAccess;
    if (next && !is2faVerified) {
      swal(
        "Verification Required",
        "Please verify your 2FA first before enabling it.",
        "warning",
      );
      return;
    }
    setIs2FaAccess(next);
  };

  const toggleMethod = (key) => {
    if (key === "sms" && !profile?.user?.phone) {
      swal(
        "Phone Number Not Found",
        "Please set your phone number first from the reset button.",
        "error",
      );
      return;
    }
    setTwoFA((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!twoFA.sms && !twoFA.email && !authenticatorEnabled) {
      swal(
        "Error",
        "At least one authentication method (SMS, Email, or Authenticator app) must be enabled.",
        "error",
      );
      return;
    }
    try {
      setSaving(true);
      const payload = {
        sms2FaEnabled: twoFA.sms,
        email2FaEnabled: twoFA.email,
        is2FaAccess,
      };
      const res = await twoFactorSettings(payload);
      swal(
        "Success",
        `${res?.data?.message || "2FA settings updated."}`,
        "success",
      );
    } catch (err) {
      console.error("Error updating 2FA settings:", err);
      swal("Error", "Failed to update 2FA settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="settings-panel active" data-panel="security">
        <div className="page-header">
          <div>
            <div className="page-title">Security</div>
            <div className="page-subtitle">
              Two-factor authentication, sessions, and password.
            </div>
          </div>
          <div className="page-actions">
            <button
              type="button"
              className="btn primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>

        {/* Unverified-2FA notice — driven by real profile state */}
        {!is2faVerified && (
          <div className="alert alert-warn">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Set up two-factor authentication to secure your account.
          </div>
        )}

        {/* ── Multi-factor authentication (REAL — wired to twoFactorSettings) ── */}
        <div className="card">
          <div className="card-section">
            <div className="card-title">Multi-factor authentication</div>
            <div className="card-subtitle">
              Add a second step at sign-in to keep patient data safe.
            </div>
          </div>

          {canManageMasterSwitch && (
            <div className="row">
              <div className="row-text">
                <div className="row-title">
                  Enable two-factor authentication
                </div>
                <div className="row-subtitle">
                  Require an extra verification step during login.
                </div>
              </div>
              <button
                type="button"
                className={`switch${is2FaAccess ? " on" : ""}`}
                aria-pressed={is2FaAccess}
                onClick={toggleMaster}
              />
            </div>
          )}

          <div className="row">
            <div className="row-text">
              <div className="row-title">SMS</div>
              <div className="row-subtitle">
                One-time code by text to your verified phone.
                {profile?.user?.phone ? (
                  <>
                    {" "}
                    <strong style={{ color: "#404040" }}>
                      {profile.user.phone}
                    </strong>{" "}
                    <span
                      className={`badge ${
                        profile?.user?.isPhoneVerified
                          ? "badge-success"
                          : "badge-danger"
                      }`}
                    >
                      {profile?.user?.isPhoneVerified
                        ? "Verified"
                        : "Not verified"}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button type="button" className="btn sm" onClick={openSetupModal}>
                Reset
              </button>
              <button
                type="button"
                className={`switch${twoFA.sms ? " on" : ""}`}
                aria-pressed={twoFA.sms}
                onClick={() => toggleMethod("sms")}
              />
            </div>
          </div>

          <div className="row">
            <div className="row-text">
              <div className="row-title">Email</div>
              <div className="row-subtitle">
                One-time code by email.
                {profile?.user?.email ? (
                  <>
                    {" "}
                    <strong style={{ color: "#404040" }}>
                      {profile.user.email}
                    </strong>
                  </>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              className={`switch${twoFA.email ? " on" : ""}`}
              aria-pressed={twoFA.email}
              onClick={() => toggleMethod("email")}
            />
          </div>

          <div className="row">
            <div className="row-text">
              <div className="row-title">Authenticator app</div>
              <div className="row-subtitle">
                TOTP codes via Google Authenticator, Microsoft Authenticator, etc.
              </div>
            </div>
            <button
              type="button"
              className={`switch${authenticatorEnabled ? " on" : ""}`}
              aria-pressed={authenticatorEnabled}
              onClick={() => {
                setAuthenticatorModalMode(
                  authenticatorEnabled ? "disable" : "setup",
                );
                openAuthenticatorModal();
              }}
            />
          </div>
        </div>

        {/* ── Password — wired to POST /auth/change-password ── */}
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            handleChangePassword();
          }}
        >
          <div className="card-section">
            <div className="card-title">Password</div>
            <div className="card-subtitle">
              Change the password you use to sign in. You'll need your current
              password to confirm it's you. Updating it signs you out on every
              other device — you'll stay signed in here.
            </div>
          </div>
          <div className="card-section">
            {pwdSuccess && (
              <div className="alert alert-success" role="status">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                {pwdSuccess}
              </div>
            )}
            {pwdFormError && (
              <div className="alert alert-error" role="alert">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {pwdFormError}
              </div>
            )}

            <div className="pwd-grid">
              <div className="pwd-field">
                <label className="label" htmlFor="pwd-current">
                  Current password
                </label>
                <div className="pwd-input">
                  <input
                    id="pwd-current"
                    className={`input${pwdErrors.current ? " has-error" : ""}`}
                    type={pwdVisible.current ? "text" : "password"}
                    placeholder="••••••••••"
                    autoComplete="current-password"
                    value={pwd.current}
                    onChange={setPwdField("current")}
                    aria-invalid={!!pwdErrors.current}
                  />
                  <button
                    type="button"
                    className="pwd-reveal"
                    onClick={() => toggleVisible("current")}
                    aria-label={
                      pwdVisible.current
                        ? "Hide current password"
                        : "Show current password"
                    }
                  >
                    <EyeIcon off={pwdVisible.current} />
                  </button>
                </div>
                {pwdErrors.current && (
                  <div className="pwd-error">{pwdErrors.current}</div>
                )}
              </div>

              <div className="pwd-field">
                <label className="label" htmlFor="pwd-new">
                  New password
                </label>
                <div className="pwd-input">
                  <input
                    id="pwd-new"
                    className={`input${pwdErrors.next ? " has-error" : ""}`}
                    type={pwdVisible.next ? "text" : "password"}
                    autoComplete="new-password"
                    value={pwd.next}
                    onChange={setPwdField("next")}
                    aria-invalid={!!pwdErrors.next}
                  />
                  <button
                    type="button"
                    className="pwd-reveal"
                    onClick={() => toggleVisible("next")}
                    aria-label={
                      pwdVisible.next
                        ? "Hide new password"
                        : "Show new password"
                    }
                  >
                    <EyeIcon off={pwdVisible.next} />
                  </button>
                </div>
                {pwd.next && (
                  <>
                    <div className={`pwd-meter s${score}`} aria-hidden="true">
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="pwd-meter-label">
                      Strength: {SCORE_LABELS[score]}
                    </div>
                  </>
                )}
                {pwdErrors.next && (
                  <div className="pwd-error">{pwdErrors.next}</div>
                )}
              </div>

              <div className="pwd-field">
                <label className="label" htmlFor="pwd-confirm">
                  Confirm new password
                </label>
                <div className="pwd-input">
                  <input
                    id="pwd-confirm"
                    className={`input${pwdErrors.confirm ? " has-error" : ""}`}
                    type={pwdVisible.confirm ? "text" : "password"}
                    autoComplete="new-password"
                    value={pwd.confirm}
                    onChange={setPwdField("confirm")}
                    aria-invalid={!!pwdErrors.confirm}
                  />
                  <button
                    type="button"
                    className="pwd-reveal"
                    onClick={() => toggleVisible("confirm")}
                    aria-label={
                      pwdVisible.confirm
                        ? "Hide confirmed password"
                        : "Show confirmed password"
                    }
                  >
                    <EyeIcon off={pwdVisible.confirm} />
                  </button>
                </div>
                {pwdErrors.confirm && (
                  <div className="pwd-error">{pwdErrors.confirm}</div>
                )}
              </div>
            </div>

            <ul className="pwd-rules">
              {rules.map((r) => (
                <li key={r.key} className={r.ok ? "ok" : ""}>
                  <CheckIcon ok={r.ok} />
                  {r.label}
                </li>
              ))}
            </ul>
            <div className="field-hint" style={{ marginTop: 10 }}>
              For a stronger password, use 12+ characters and mix in a symbol.
            </div>
          </div>
          <div className="card-footer">
            <button type="submit" className="btn primary" disabled={savingPwd}>
              {savingPwd ? "Updating..." : "Update password"}
            </button>
          </div>
        </form>

        {/* ── Active sessions — wired to /auth/sessions ── */}
        <div className="card">
          <div className="card-section">
            <div className="card-title">Active sessions</div>
            <div className="card-subtitle">
              Devices signed in to your account. If you don't recognise one,
              sign it out and change your password.
            </div>
          </div>

          <div className="card-section">
            {sessionsLoading ? (
              <div className="session-empty">Loading your devices...</div>
            ) : sessionsError ? (
              <div className="alert alert-error" role="alert">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {sessionsError}
              </div>
            ) : sessions.length === 0 ? (
              <div className="session-empty">
                No active devices to show yet.
              </div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  className={`session-row${s.current ? " is-current" : ""}`}
                >
                  <div className="session-id">
                    <span className="session-ico">
                      <DeviceIcon type={s.deviceType} />
                    </span>
                    <div>
                      <div className="row-title">
                        {s.browser} on {s.os}
                        {s.current && (
                          <>
                            {" "}
                            <span className="badge badge-success">
                              This device
                            </span>
                          </>
                        )}
                      </div>
                      <div className="row-subtitle">
                        {s.current
                          ? "Active now"
                          : `Last active ${moment(s.lastActiveAt).fromNow()}`}
                        {s.ip ? ` · ${s.ip}` : ""}
                        {s.createdAt
                          ? ` · Signed in ${moment(s.createdAt).format(
                              "MMM D, YYYY",
                            )}`
                          : ""}
                      </div>
                    </div>
                  </div>
                  {!s.current && (
                    <button
                      type="button"
                      className="btn sm ghost-danger"
                      onClick={() => handleRevokeSession(s)}
                      disabled={revoking === s.id}
                    >
                      {revoking === s.id ? "Signing out..." : "Sign out"}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="card-footer">
            <button
              type="button"
              className="btn danger-ghost"
              onClick={handleRevokeOthers}
              disabled={otherSessionCount === 0 || revoking === "others"}
            >
              {revoking === "others"
                ? "Signing out..."
                : otherSessionCount === 0
                  ? "No other devices signed in"
                  : `Sign out all other sessions (${otherSessionCount})`}
            </button>
          </div>
        </div>
      </div>

      {/* Reuse the existing setup/reset flow — real wiring, not rebuilt */}
      <SetupTwoFactorModal
        opened={setupModalOpened}
        close={closeSetupModal}
        profile={profile}
        isSettingPage={true}
      />

      <SetupAuthenticatorModal
        opened={authenticatorModalOpened}
        close={closeAuthenticatorModal}
        profile={profile}
        mode={authenticatorModalMode}
        onSuccess={setAuthenticatorEnabled}
      />
    </>
  );
}
